import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CompanyAnalytics = ({ companyId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/admin/companies/${companyId}/analytics`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [companyId]);

  if (loading) return <p style={{ color: '#fff' }}>Analiz verileri yükleniyor...</p>;
  if (!data) return <p style={{ color: '#fff' }}>Veri bulunamadı.</p>;

  const financeMonths = data.monthly_finance.map(d => d.month);
  const financeIncomes = data.monthly_finance.map(d => parseFloat(d.income));
  const financeExpenses = data.monthly_finance.map(d => parseFloat(d.expense));

  // Gelir Gider Bar Grafiği Verisi
  const financeChartData = {
    labels: financeMonths,
    datasets: [
      {
        label: 'Gelir (₺)',
        data: financeIncomes,
        backgroundColor: '#10B981',
        borderRadius: 6,
      },
      {
        label: 'Gider (₺)',
        data: financeExpenses,
        backgroundColor: '#EF4444',
        borderRadius: 6,
      }
    ]
  };

  // Randevu ve Çekim Aylık Trend Çizgi Grafiği Verisi
  const apptMonths = data.monthly_appointments.map(d => d.month);
  const apptCounts = data.monthly_appointments.map(d => parseInt(d.count));
  const shootCounts = data.monthly_shootings.map(d => parseInt(d.count));

  // Ortak ayları birleştir
  const allMonthsSet = new Set([...apptMonths, ...data.monthly_shootings.map(d => d.month)]);
  const allMonthsSorted = Array.from(allMonthsSet).sort();

  const apptDataMerged = allMonthsSorted.map(month => {
    const found = data.monthly_appointments.find(d => d.month === month);
    return found ? parseInt(found.count) : 0;
  });

  const shootDataMerged = allMonthsSorted.map(month => {
    const found = data.monthly_shootings.find(d => d.month === month);
    return found ? parseInt(found.count) : 0;
  });

  const operationChartData = {
    labels: allMonthsSorted,
    datasets: [
      {
        label: 'Randevular',
        data: apptDataMerged,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.3,
        fill: true
      },
      {
        label: 'Çekimler',
        data: shootDataMerged,
        borderColor: '#8B5CF6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.3,
        fill: true
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#9CA3AF'
        }
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: { color: '#9CA3AF' }
      },
      y: {
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: { color: '#9CA3AF' }
      }
    }
  };

  return (
    <div style={{ color: '#fff' }}>
      <h3 style={{ marginTop: 0, marginBottom: '24px', fontSize: '16px', fontWeight: 600 }}>📊 Finansal ve Operasyonel Analiz</h3>

      {/* KPI Kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <div style={kpiCardStyle}>
          <span style={kpiTitleStyle}>Toplam Gelir</span>
          <span style={{ ...kpiValueStyle, color: '#10B981' }}>{data.kpi.total_income.toLocaleString('tr-TR')} ₺</span>
        </div>
        <div style={kpiCardStyle}>
          <span style={kpiTitleStyle}>Toplam Gider</span>
          <span style={{ ...kpiValueStyle, color: '#EF4444' }}>{data.kpi.total_expense.toLocaleString('tr-TR')} ₺</span>
        </div>
        <div style={kpiCardStyle}>
          <span style={kpiTitleStyle}>Net Kar / Zarar</span>
          <span style={{ ...kpiValueStyle, color: data.kpi.net_profit >= 0 ? '#10B981' : '#EF4444' }}>
            {data.kpi.net_profit.toLocaleString('tr-TR')} ₺
          </span>
        </div>
        <div style={kpiCardStyle}>
          <span style={kpiTitleStyle}>Toplam Randevu</span>
          <span style={{ ...kpiValueStyle, color: '#3B82F6' }}>{data.kpi.appointments_count} Adet</span>
        </div>
        <div style={kpiCardStyle}>
          <span style={kpiTitleStyle}>Toplam Çekim</span>
          <span style={{ ...kpiValueStyle, color: 'var(--secondary)' }}>{data.kpi.shootings_count} Adet</span>
        </div>
      </div>

      {/* Grafikler */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div style={chartWrapperStyle}>
          <h4 style={chartTitleStyle}>💰 Aylık Gelir vs Gider Trendi</h4>
          <div style={{ height: '300px' }}>
            <Bar data={financeChartData} options={chartOptions} />
          </div>
        </div>

        <div style={chartWrapperStyle}>
          <h4 style={chartTitleStyle}>📅 Aylık Operasyon Dağılımı</h4>
          <div style={{ height: '300px' }}>
            <Line data={operationChartData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

const kpiCardStyle = {
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: '12px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const kpiTitleStyle = {
  fontSize: '12px',
  color: '#9CA3AF',
  textTransform: 'uppercase',
  fontWeight: 600
};

const kpiValueStyle = {
  fontSize: '18px',
  fontWeight: 700
};

const chartWrapperStyle = {
  background: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  borderRadius: '16px',
  padding: '24px'
};

const chartTitleStyle = {
  margin: '0 0 16px 0',
  fontSize: '14px',
  fontWeight: 600,
  color: '#E5E7EB'
};

export default CompanyAnalytics;
